import React, { useMemo, useState } from 'react';
import { Add, Remove } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import {
  DOCUMENT_TYPES_BIDDING,
  DOCUMENT_TYPES_CONSTRUCTION,
  DOCUMENT_TYPES_DESIGN,
  getDocumentIdentifier,
  getUserFriendlyDocumentTemplateNamePlural,
  getUserFriendlyDocumentTemplateNameSingular,
} from '../../scripts/utils';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import {
  DocumentTemplateType,
  INumberedDocumentViewWithRagFlag,
} from '../../api-client/autogenerated';
import { useAppUtilsContext } from '../AppUtilsContext';
import { Collapse } from '@material-ui/core';
import { ascendingComparator } from '../document-index/DocumentIndexUtils';
import DocumentSearchItem from './DocumentSearchItem';
import DesignIcon from '../icons/Design-icon';
import BiddingIcon from '../icons/Bidding-icon';
import ConstructionIcon from '../icons/Construction-icon';
import CloseOutIcon from '../icons/CloseOut-icon';

const getIconFromDocumentTemplateType = (documentType: DocumentTemplateType) => {
  const props = {
    fill: '#0247B9',
    width: 26,
    height: 26,
    style: { marginRight: 9, marginLeft: -2 },
  };

  if (DOCUMENT_TYPES_DESIGN.includes(documentType)) {
    return <DesignIcon {...props} />;
  } else if (DOCUMENT_TYPES_BIDDING.includes(documentType)) {
    return <BiddingIcon {...props} />;
  } else if (DOCUMENT_TYPES_CONSTRUCTION.includes(documentType)) {
    return <ConstructionIcon {...props} />;
  } else {
    return <CloseOutIcon {...props} />;
  }
};

interface DocumentTypeSearchItemProps {
  documentTemplateId: string;
  documents: INumberedDocumentViewWithRagFlag[];
  searchTerm: string;
  keywordSearchTerms: string[];
}

export default function DocumentTypeSearchItem(props: DocumentTypeSearchItemProps) {
  const { documentTemplateId, documents, searchTerm, keywordSearchTerms } = props;

  const {
    documentTemplates: { getTemplateName },
  } = useAppUtilsContext();

  const [open, setOpen] = useState(true);

  const documentType = useMemo(() => getTemplateName(documentTemplateId), [documentTemplateId]);

  const documentsWithUserFriendlyTitle = useMemo(
    () =>
      documents.map((d) => ({
        ...d,
        userFriendlyTitle: `${getUserFriendlyDocumentTemplateNameSingular(
          documentType,
        )} ${getDocumentIdentifier(d, documentType)}`,
      })),
    [documents, documentType],
  );

  return (
    <Paper
      style={{
        marginBottom: 8,
        boxShadow: '1px 1px 10px 1px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'flex',
          position: 'relative',
          alignItems: 'center',
          padding: 16,
          borderBottom: '1px solid #DCDCDC',
          cursor: 'pointer',
          zIndex: 10,
        }}
      >
        {getIconFromDocumentTemplateType(documentType)}
        <Typography style={{ fontSize: '18px', lineHeight: '24px', fontWeight: 700 }}>
          {getUserFriendlyDocumentTemplateNamePlural(documentType)} ({documents.length})
        </Typography>
        <div style={{ display: 'inline-flex', flexGrow: 100 }} />
        <IconButton style={{ padding: 0 }}>{open ? <Remove /> : <Add />}</IconButton>
      </div>
      <Collapse in={open}>
        {documentsWithUserFriendlyTitle
          .sort((a, b) => ascendingComparator(a, b, 'userFriendlyTitle'))
          .map((doc) => (
            <DocumentSearchItem
              key={doc.id}
              documentType={documentType}
              document={doc}
              searchTerm={searchTerm}
              keywordSearchTerms={keywordSearchTerms}
            />
          ))}
      </Collapse>
    </Paper>
  );
}
